import { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, getPlatformsByCategory } from '@/mocks/platforms';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeCategory]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeCategory) || categories[0],
    [activeCategory],
  );

  const filteredPlatforms = useMemo(
    () => getPlatformsByCategory(activeCategory),
    [activeCategory],
  );

  const seoTitle = `${currentCategory.name} - Po文网站推荐 | Po文导航`;
  const seoDescription = `浏览${currentCategory.name}类po文网站推荐。${currentCategory.description}。Po文导航为你精选优质原创内容发布平台，助力创作者找到最佳创作家园。`;
  const seoKeywords = `po文网站,po文网站推荐,${currentCategory.name},创作者平台,内容发布平台`;

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={`https://powen.example.com/category/${activeCategory}`}
      />
      <Navbar />
      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: currentCategory.name },
              ]}
            />
            <h1 className="mt-3 text-2xl md:text-4xl font-bold font-heading text-foreground-900">
              {currentCategory.name}
            </h1>
            <p className="mt-2 text-foreground-500">{currentCategory.description}</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex items-center gap-2 flex-wrap pb-6 border-b border-background-200">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveCategory(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} mr-1.5 text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredPlatforms.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无平台，请查看其他分类</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8" data-product-shop>
              {filteredPlatforms.map((platform) => (
                <a
                  key={platform.id}
                  href={platform.url}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="group bg-background-50 border border-background-200 rounded-xl p-6 hover:border-primary-300 transition-all duration-300 cursor-pointer block"
                >
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center">
                      <i className="ri-book-read-line text-xl text-primary-600"></i>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className={`ri-star-fill text-xs ${
                            star <= Math.floor(platform.rating)
                              ? 'text-accent-500'
                              : 'text-background-300'
                          }`}
                        ></i>
                      ))}
                      <span className="ml-1 text-xs font-semibold text-foreground-700">
                        {platform.rating}
                      </span>
                    </div>
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                    {platform.name}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                    {platform.description}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {platform.features.map((feat) => (
                      <span
                        key={feat}
                        className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700"
                      >
                        {feat}
                      </span>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-background-200 flex items-center justify-between text-xs text-foreground-400">
                    <span>{platform.pricing}</span>
                    <span>注册难度: {platform.difficulty}</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}